import { Component, EventEmitter, Input, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap'
import { addDays, subDays } from 'date-fns'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { isEqual } from 'lodash-es'
import { Subscription } from 'rxjs'
import { LocaleAwareNgbDateParserFormatter } from '../../vendors/ngb.vendor.module'
import { v4 as uuidv4 } from 'uuid'

@Component({
    selector: 'app-date-range-input',
    templateUrl: './date-range-input.component.html',
    providers: [
        { provide: NgbDateParserFormatter, useClass: LocaleAwareNgbDateParserFormatter }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DateRangeInputComponent {
  @Input() readonly = false
  isDateRangeJoined = false
  joinDateRangeCheckBoxId = uuidv4()

  @Input() pastReference: Date | undefined | null

  _enableJoinedDateBehavior = false
  @Input() set enableJoinedDateBehavior (isEnabled: boolean) {
    // filter only the first time this is set
    if(isEnabled && !this._enableJoinedDateBehavior) {
      this._enableJoinedDateBehavior = true
      this.isDateRangeJoined = true
      this.joinDateRange()
    }
  }
  get enableJoinedDateBehavior (): boolean {
    return this._enableJoinedDateBehavior
  }

  @Input() timezone: string
  @Input() dateFormat: string

  _minDateAsNgb: NgbDateStruct | undefined
  @Input() minDate: Date | undefined

  _maxDateAsNgb: NgbDateStruct | undefined
  @Input() maxDate: Date | undefined

  @Input() zonedDateRange: [Date, Date] | null | undefined

  zonedDateRangeForm = new FormGroup({
    start: new FormControl<NgbDateStruct>({ year: NaN, month: NaN, day: NaN, }, { nonNullable: true }),
    end  : new FormControl<NgbDateStruct>({ year: NaN, month: NaN, day: NaN, }, { nonNullable: true }),
  })

  @Output() zonedDateRangeChange = new EventEmitter<[Date, Date]>();

  subscriptions: Subscription[] = [
    this.zonedDateRangeForm.valueChanges.subscribe(value => {
      // Allow disabled behavior through form control while getting the displayed value when joinedDateRange is enabled
      const start = this.zonedDateRangeForm.controls.start.getRawValue()
      const end = value.end
      if(this.timezone === undefined) throw Error('timezone is undefined')
      if(!start || !end) return
      const zonedDateRange: [Date, Date] = [
        start && !isNaN(start.year) && !isNaN(start.month) && !isNaN(start.day)
          ? zonedTimeToUtc(new Date(start.year, start.month - 1, start.day), this.timezone)
          : new Date('invalid date'),
        end && !isNaN(end.year) && !isNaN(end.month) && !isNaN(end.day)
          ? zonedTimeToUtc(addDays(new Date(end.year, end.month - 1, end.day), 1), this.timezone)
          : new Date('invalid date')
      ]
      this.zonedDateRangeChange.emit(zonedDateRange)
    })
  ]

  ngOnDestroy (): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
  }

  ngOnChanges (changes: SimpleChanges): void {
    if(!this.timezone) throw Error('timezone undefined')
    if(changes.minDate || changes.maxDate){
      this.rangeDateUpdate()
    }
    if(changes.zonedDateRange) {
      const dateRange = changes.zonedDateRange.currentValue
      if(!this.timezone) throw Error('timezone undefined')
      if(dateRange === undefined || dateRange === null) {
        this.zonedDateRangeForm.setValue({
          start: { year: NaN, month: NaN, day: NaN, },
          end  : { year: NaN, month: NaN, day: NaN, },
        })
      } else if(isNaN(dateRange[0].valueOf()) || isNaN(dateRange[1].valueOf())) {
        return
      } else {
        const zonedDate0 = utcToZonedTime(dateRange[0].valueOf(), this.timezone)
        const zonedDate1 = subDays(utcToZonedTime(dateRange[1].valueOf(), this.timezone), 1)
        const formValueFromInputs = {
          start: { year: zonedDate0.getFullYear(), month: zonedDate0.getMonth() + 1, day: zonedDate0.getDate(), },
          end  : { year: zonedDate1.getFullYear(), month: zonedDate1.getMonth() + 1, day: zonedDate1.getDate(), }
        }
        if(!isEqual(this.zonedDateRangeForm.getRawValue(), formValueFromInputs)) {
          this.zonedDateRangeForm.setValue(formValueFromInputs)
        }
      }
    }
    if(changes.pastReference) {
      this.joinDateRange()
    }
  }

  private rangeDateUpdate () {
    if (this.minDate === undefined || this.minDate === null || isNaN(this.minDate.valueOf())) {
      this._minDateAsNgb = {
        year : NaN,
        month: NaN,
        day  : NaN,
      }
    } else {
      const zonedMinDate = utcToZonedTime(this.minDate.valueOf(), this.timezone)
      this._minDateAsNgb = {
        year : zonedMinDate.getFullYear(),
        month: zonedMinDate.getMonth() + 1,
        day  : zonedMinDate.getDate(),
      }
    }
    if (this.maxDate === undefined || this.maxDate === null || isNaN(this.maxDate.valueOf())) {
      this._maxDateAsNgb = {
        year : NaN,
        month: NaN,
        day  : NaN,
      }
    } else {
      const zonedMaxDate = utcToZonedTime(this.maxDate.valueOf(), this.timezone)
      this._maxDateAsNgb = {
        year : zonedMaxDate.getFullYear(),
        month: zonedMaxDate.getMonth() + 1,
        day  : zonedMaxDate.getDate(),
      }
    }
  }

  setDateToNow (): void {
    if(this.timezone === undefined) throw Error('timezone is undefined')
    const now = subDays(utcToZonedTime(Date.now(), this.timezone), 1)
    this.zonedDateRangeForm.controls.end.setValue({
      year : now.getFullYear(),
      month: now.getMonth() + 1,
      day  : now.getDate(),
    })
  }
  joinDateRange (): void {
    if(this.timezone === undefined) throw Error('timezone is undefined')
    if(this.pastReference === undefined) throw Error('pastReference is undefined')
    if(this.pastReference === null) throw Error('pastReference is null')
    if(this.isDateRangeJoined) {
      this.zonedDateRangeForm.controls.start.disable()
      const pastReference = utcToZonedTime(this.pastReference.valueOf(), this.timezone)
      this.zonedDateRangeForm.controls.start.setValue({
        year : pastReference.getFullYear(),
        month: pastReference.getMonth() + 1,
        day  : pastReference.getDate(),
      })
    } else {
      this.zonedDateRangeForm.controls.start.enable()
    }
  }
}
